import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';

import { registerUser, registerFirm, verifyRegistrationOtp } from '../utils/apiUtils';
import { loginUser, validateUser, loginUserWithGoogle } from '../redux/authSlice';
import { ROUTE_LOGIN } from '../constants';
import { isValidEmail, isValidPassword, isValidPhone, validateField } from '../utils/validationUtils';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { getUserReadableError } from '../utils/utils';
import AuthLayout from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import TurnstileWidget, { TURNSTILE_SITE_KEY } from '../components/TurnstileWidget';

const GOOGLE_CLIENT_ID = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const ONBOARDING_PENDO_PARAMS = '?pendo=LA7k-x1xoQDk3OBPh1pRGoe5kU0&step=Z6U9lrOAqIVyLsjvA6_8071mRGY';

const CustomGoogleLogin = ({ turnstileToken, onTokenUsed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (codeResponse) => {
    try {
      await dispatch(loginUserWithGoogle(codeResponse, navigate, undefined, turnstileToken));
    } catch (error) {
      console.error('Google sign-up failed:', error);
      onTokenUsed();
      showErrorMessage(getUserReadableError(error) ?? 'Google sign-up failed');
    }
  };

  const handleGoogleError = () => {
    showErrorMessage('Google sign-up was unsuccessful. Please try again.');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    flow: 'auth-code',
  });

  return (
    <button onClick={() => googleLogin()} className="google-button">
      <svg className="google-icon" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.42-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
        <path fill="none" d="M0 0h48v48H0z"></path>
      </svg>
      <span>Continue with Google</span>
    </button>
  );
};

const RegisterStep1 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 'email' -> 'otp' -> 'profile'
  const [stage, setStage] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [code, setCode] = useState(null); // registration token released after OTP verify
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    firmName: '',
    phone: '',
    password: '',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [profileErrors, setProfileErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [domainWorkspace, setDomainWorkspace] = useState(null);

  const isEmailValid = isValidEmail(email);
  const needsChallenge = !!TURNSTILE_SITE_KEY && !turnstileToken;
  const refreshChallenge = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileKey((k) => k + 1);
  }, []);
  const isOtpValid = /^\d{6}$/.test(otp.trim());

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const startResendCooldown = () => setResendIn(RESEND_COOLDOWN_SECONDS);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setProfileErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    if (!isEmailValid) {
      showErrorMessage('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const resp = await registerUser({ email, turnstileToken });
      setDomainWorkspace(resp.data?.item?.domainWorkspace ?? null);
      showSuccessMessage(`We sent a 6-digit code to ${email}.`);
      setStage('otp');
      startResendCooldown();
    } catch (error) {
      console.error('RegisterStep1 (email) failed:', error);
      showErrorMessage(getUserReadableError(error) ?? 'Could not send verification code. Please try again.');
    } finally {
      refreshChallenge();
      setIsLoading(false);
    }
  };

  const onSubmitOtp = async (e) => {
    e.preventDefault();
    if (!isOtpValid) {
      showErrorMessage('Please enter the 6-digit code from your email');
      return;
    }
    setIsLoading(true);
    try {
      const resp = await verifyRegistrationOtp({ email, code: otp.trim() });
      const releasedCode = resp.data?.item?.code;
      if (!releasedCode) {
        throw new Error('Verification did not return a registration token.');
      }
      setCode(releasedCode);
      setStage('profile');
    } catch (error) {
      console.error('RegisterStep1 (otp) failed:', error);
      showErrorMessage(getUserReadableError(error) ?? 'Invalid or expired code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onResendOtp = async () => {
    if (resendIn > 0 || isLoading) return;
    setIsLoading(true);
    try {
      await registerUser({ email });
      showSuccessMessage('A new code is on its way.');
      startResendCooldown();
    } catch (error) {
      showErrorMessage(getUserReadableError(error) ?? 'Could not resend the code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitProfile = async (e) => {
    e.preventDefault();

    const errors = {};
    let err = validateField({ isRequired: true, maxLength: 50 }, formData.firstName, 'First name');
    if (err) errors.firstName = err;
    err = validateField({ isRequired: true, maxLength: 50 }, formData.lastName, 'Last name');
    if (err) errors.lastName = err;
    err = validateField({ isRequired: true, maxLength: 100 }, formData.firmName, 'Business / Company name');
    if (err) errors.firmName = err;

    const trimmedPhone = formData.phone.trim();
    if (trimmedPhone && !isValidPhone(trimmedPhone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    err = validateField({ isPassword: true }, formData.password, 'Password');
    if (err) {
      errors.password = err;
    } else if (!isValidPassword(formData.password)) {
      errors.password = 'Invalid password';
    }
    if (Object.keys(errors).length) {
      setProfileErrors(errors);
      showErrorMessage(Object.values(errors)[0]);
      return;
    }

    setProfileErrors({});
    setIsLoading(true);
    try {
      const registerResponse = await registerFirm({
        ...formData,
        phone: trimmedPhone || null,
        confirmPassword: formData.password,
        privacyPolicyAccepted: true,
        code,
      });
      showSuccessMessage('Account verified successfully 🎉');

      const loginEmail = registerResponse.data?.user?.email || email;
      const { password } = formData;

      if (loginEmail && password) {
        await dispatch(loginUser({ email: loginEmail, password }, navigate, () => true));
        const result = await dispatch(validateUser());
        if (validateUser.fulfilled.match(result)) {
          navigate('/onboarding' + ONBOARDING_PENDO_PARAMS);
        } else {
          console.error('validateUser failed after registration:', result.error);
          navigate('/onboarding' + ONBOARDING_PENDO_PARAMS);
        }
      } else {
        navigate('/onboarding' + ONBOARDING_PENDO_PARAMS);
      }
    } catch (error) {
      console.error('RegisterStep1 (profile) failed:', error);
      showErrorMessage(getUserReadableError(error) || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const profileTitle = stage === 'profile' ? 'Almost there — last step' : undefined;

  return (
    <AuthLayout
      icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
      title={profileTitle}
    >
      <div className="auth-stack">
        {stage === 'email' && (
          <>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <CustomGoogleLogin turnstileToken={turnstileToken} onTokenUsed={refreshChallenge} />
            </GoogleOAuthProvider>

            <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />

            <div className="auth-separator">
              <div className="auth-separator-line">
                <div className="auth-separator-border" />
              </div>
              <div className="auth-separator-text">
                <span className="auth-separator-span">or</span>
              </div>
            </div>

            <form onSubmit={onSubmitEmail} className="auth-form">
              <div className="input-group">
                <label htmlFor="email" className="input-label">Email address</label>
                <input
                  type="email"
                  id="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="input-field"
                  required
                />
                {email.length > 0 && !isEmailValid && (
                  <p className="auth-validation-error">Please enter a valid email address</p>
                )}
              </div>

              <div className="pt-2">
                <button type="submit" className="auth-button" disabled={!isEmailValid || isLoading || needsChallenge}>
                  {isLoading ? 'Sending...' : 'Send verification code'}
                </button>
              </div>

              <p className="text-center pt-2" style={{ fontSize: '13px', color: '#6B7280' }}>
                By continuing, you agree to our{' '}
                <a href="https://www.typehero.ai/terms" target="_blank" rel="noreferrer" style={{ color: '#1c51a6', textDecoration: 'none' }}>Terms</a>{' '}and{' '}
                <a href="https://www.typehero.ai/privacy-policy" target="_blank" rel="noreferrer" style={{ color: '#1c51a6', textDecoration: 'none' }}>Privacy Policy</a>.
              </p>
            </form>

            <p className="text-center pt-2" style={{ fontSize: '14px', color: '#6B7280' }}>
              Already have an account?{' '}
              <Link to={ROUTE_LOGIN} className="auth-footer-link">Sign in</Link>
            </p>
          </>
        )}

        {stage === 'otp' && (
          <form onSubmit={onSubmitOtp} className="auth-form">
            <p className="text-center" style={{ fontSize: '14px', color: '#6B7280' }}>
              Enter the {OTP_LENGTH}-digit code we sent to <strong>{email}</strong>.
            </p>

            {domainWorkspace && (
              <p className="text-center" style={{ fontSize: '14px', color: '#6B7280' }}>
                <strong>{domainWorkspace.firmName}</strong> already uses TypeHero with this email domain. To join it, ask {domainWorkspace.ownerEmail} to invite you, or continue to create a separate workspace.
              </p>
            )}

            <div className="input-group">
              <label htmlFor="otp" className="input-label">Verification code</label>
              <input
                type="text"
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="input-field"
                style={{ letterSpacing: '6px', textAlign: 'center', fontSize: '20px' }}
                required
              />
            </div>

            <div className="pt-2">
              <button type="submit" className="auth-button" disabled={!isOtpValid || isLoading}>
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>

            <p className="text-center pt-2" style={{ fontSize: '14px', color: '#6B7280' }}>
              Didn&rsquo;t get it?{' '}
              <button
                type="button"
                onClick={onResendOtp}
                disabled={resendIn > 0 || isLoading}
                className="auth-footer-link"
                style={{ background: 'none', border: 'none', cursor: resendIn > 0 ? 'default' : 'pointer', padding: 0 }}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => { setStage('email'); setOtp(''); }}
                className="auth-footer-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Change email
              </button>
            </p>
          </form>
        )}

        {stage === 'profile' && (
          <form onSubmit={onSubmitProfile} className="auth-form">
            <div className="input-row" style={{ gap: '12px' }}>
              <div className="input-group">
                <label htmlFor="firstName" className="input-label">First name<span className="required">*</span></label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleProfileChange}
                  autoComplete="given-name"
                  className="input-field"
                  aria-invalid={!!profileErrors.firstName}
                  required
                />
                {profileErrors.firstName && <p className="auth-validation-error">{profileErrors.firstName}</p>}
              </div>

              <div className="input-group">
                <label htmlFor="lastName" className="input-label">Last name<span className="required">*</span></label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleProfileChange}
                  autoComplete="family-name"
                  className="input-field"
                  aria-invalid={!!profileErrors.lastName}
                  required
                />
                {profileErrors.lastName && <p className="auth-validation-error">{profileErrors.lastName}</p>}
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="firmName" className="input-label">Business / Company name<span className="required">*</span></label>
              <input
                type="text"
                id="firmName"
                name="firmName"
                value={formData.firmName}
                onChange={handleProfileChange}
                autoComplete="organization"
                className="input-field"
                aria-invalid={!!profileErrors.firmName}
                required
              />
              {profileErrors.firmName && <p className="auth-validation-error">{profileErrors.firmName}</p>}
              <p className="auth-helper-text">Used for billing — you can change it later.</p>
            </div>

            <div className="input-group">
              <label htmlFor="phone" className="input-label">Phone number (optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleProfileChange}
                autoComplete="tel"
                className="input-field"
                aria-invalid={!!profileErrors.phone}
              />
              {profileErrors.phone && <p className="auth-validation-error">{profileErrors.phone}</p>}
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">Password<span className="required">*</span></label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleProfileChange}
                placeholder="Create a strong password"
                showStrength={true}
                autoComplete="new-password"
                required
              />
              {profileErrors.password && <p className="auth-validation-error">{profileErrors.password}</p>}
            </div>

            <div className="pt-2">
              <button type="submit" className="auth-button" disabled={isLoading}>
                {isLoading ? 'Setting up...' : 'Enter TypeHero'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default RegisterStep1;